/**
 * PRESSED NAV — one light travels the glyph's own outline once, then goes.
 *
 * The prototype's own note (`Hubs Feed.dc.html:376-380`): "A second copy of
 * the icon mounts over the first for the duration of the press and unmounts
 * after, so the animation runs exactly once — a continuous loop turned a
 * confirmation into decoration."
 *
 * ── WHY THIS IS A MEASURED OVERLAY AND NOT PART OF THE PILL ───────────────
 * In the prototype the trace is a child of the button. Here the Wall/Stream
 * pills are rendered by `paper-view-track` INSIDE the published
 * `paper-shell-bar`, which projects only `[shell-bar-mark]` and
 * `[shell-bar-utility]` — there is no slot to put a second glyph in, and
 * `PaperNavView` carries no template. The same wall `app-glyphs.ts` describes
 * for the marks themselves.
 *
 * So the overlay lives outside the bar, reads the tab's rectangle, and draws
 * itself over the top at `position: fixed`. It never writes to Paper's DOM —
 * one `getBoundingClientRect` and nothing else — which is what keeps this from
 * being a component someone else's release can break in an interesting way.
 * The worst a Paper rename can do is return null here, and the worst THAT does
 * is skip an animation.
 *
 * ⚠ ITS REAL HOME IS THE `ricoh-paper` REPOSITORY. The prototype applies the
 * same trace to the workspace tiles as well (line 546), and those are just as
 * unreachable from here. When `paper-view-track` grows press feedback of its
 * own, delete this. Tracked as `indoor_mapping_poc-ciq.3a`.
 */

/** Where on screen to draw the copy. Viewport coordinates. */
export interface NavTraceRect {
  readonly top: number;
  readonly left: number;
  readonly width: number;
  readonly height: number;
}

/** One press, in flight. `key` exists so a second press remounts the layer. */
export interface NavTrace {
  readonly key: number;
  readonly icon: string;
  readonly rect: NavTraceRect;
}

/**
 * How long the layer stays mounted — the prototype's `_pulse` timeout.
 *
 * Longer than the .85s animation on purpose, so the last frame is drawn
 * before the element goes rather than being cut off by the unmount.
 */
export const NAV_TRACE_MS = 900;

/** Paper's own class names. Read-only knowledge of another package's DOM. */
const TAB = '.paper-view-track__tab';
const ICON = '.paper-view-track__icon';

/**
 * The rectangle of a view pill's ICON, or null if it is not on screen.
 *
 * Null for all the ordinary reasons: the view is in the overflow, the track
 * has not rendered, the id names nothing, or Paper renamed the class. Every
 * one of them means "do not animate", which is why they share an answer.
 */
export function findViewIconRect(
  root: ParentNode,
  viewId: string,
): NavTraceRect | null {
  // `CSS.escape` is not in jsdom's older shims and the ids are registry slugs,
  // so refuse anything that could break out of the attribute selector rather
  // than depending on it.
  if (!/^[\w-]+$/.test(viewId)) return null;
  const icon = root.querySelector(`${TAB}[data-view-id="${viewId}"] ${ICON}`);
  if (!icon) return null;
  const { top, left, width, height } = icon.getBoundingClientRect();
  // A zero box is an element that is present but not laid out — a collapsed
  // track, or `display: none` on a narrow viewport. Drawing a trace on it
  // would put a light in the top-left corner of the screen.
  if (width <= 0 || height <= 0) return null;
  return { top, left, width, height };
}
